import { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Wifi, WifiOff, RefreshCw, Languages, LogOut, HelpCircle, CloudOff } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLang } from "../contexts/LangContext";
import { api } from "../lib/api";
import { getQueue, flushQueue } from "../lib/offlineQueue";
import { toast } from "./ui/sonner";
import appIcon from "../assets/app-icon.png";

const DESKTOP_ITEMS = [
  { label: "Tableau de Bord", to: "/", testId: "desktop-nav-home" },
  { label: "Audits Terrain", to: "/audits", testId: "desktop-nav-audits" },
  { label: "Sites & Usines", to: "/sites", testId: "desktop-nav-sites" },
  { label: "Plan d'Actions", to: "/actions", testId: "desktop-nav-actions" },
  { label: "Analyses & Heatmap", to: "/dashboard", testId: "desktop-nav-dashboard" },
  { label: "Historique 2024-2026", to: "/historique", testId: "desktop-nav-history" },
  { label: "Rapports", to: "/rapports", testId: "desktop-nav-rapports" },
];

export default function TopBar() {
  const { user, logout } = useAuth();
  const { lang, toggleLang } = useLang();
  const [online, setOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const navigate = useNavigate();

  const refreshPending = useCallback(() => {
    getQueue().then((q) => setPendingCount(q.length)).catch(() => {});
  }, []);

  const sync = useCallback(async () => {
    if (!navigator.onLine) {
      toast.error("Toujours hors-ligne, synchronisation impossible");
      return;
    }
    const synced = await flushQueue(api);
    refreshPending();
    if (synced > 0) toast.success(`${synced} saisie(s) synchronisée(s)`);
  }, [refreshPending]);

  useEffect(() => {
    const on = () => { setOnline(true); sync(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    window.addEventListener("wcei-offline-queue-changed", refreshPending);
    refreshPending();
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("wcei-offline-queue-changed", refreshPending);
    };
  }, [sync, refreshPending]);

  const items = user?.role === "ADMIN" ? [...DESKTOP_ITEMS, { label: "Administration", to: "/admin", testId: "desktop-nav-admin" }] : DESKTOP_ITEMS;

  return (
    <header className="sticky top-0 z-30 bg-slate-900 text-white border-b border-slate-800 shadow-sm">
      <div className="flex items-center justify-between gap-3 px-3 sm:px-5 py-2.5">
        <div className="flex items-center gap-2 min-w-0">
          <img src={appIcon} alt="World Class EI" className="h-8 w-8 rounded-md shrink-0" data-testid="header-app-icon" />
          <div className="min-w-0">
            <div data-testid="header-brand-title" className="font-heading font-extrabold uppercase tracking-tight text-sm sm:text-base leading-none truncate">
              World Class EI
            </div>
            <div className="text-[10px] sm:text-xs text-slate-400 truncate">Lafarge Ciments France</div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              data-testid={it.testId}
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-emerald-600 text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
                }`
              }
            >
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <div
            data-testid="network-status-indicator"
            className={`flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-full border ${
              online ? "border-emerald-500 text-emerald-400" : "border-slate-400 text-slate-300 pulse-dot"
            }`}
          >
            {online ? <Wifi className="h-3.5 w-3.5" /> : <WifiOff className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">{online ? "En ligne" : "Mode Hors-ligne"}</span>
          </div>
          {pendingCount > 0 && (
            <div data-testid="offline-pending-badge" title={`${pendingCount} saisie(s) en attente de synchronisation`}
              className="flex items-center gap-1 text-[11px] font-mono px-2 py-1 rounded-full border border-amber-500 text-amber-400">
              <CloudOff className="h-3.5 w-3.5" />
              <span>{pendingCount}</span>
            </div>
          )}
          <button
            data-testid="sync-action-button"
            className="p-1.5 rounded-md hover:bg-slate-800 text-slate-300"
            title="Synchroniser"
            onClick={sync}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <button
            data-testid="help-nav-button"
            onClick={() => navigate("/aide")}
            className="p-1.5 rounded-md hover:bg-slate-800 text-slate-300"
            title="Aide"
          >
            <HelpCircle className="h-4 w-4" />
          </button>
          <button
            data-testid="lang-toggle-button"
            onClick={toggleLang}
            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-mono border border-slate-700 hover:bg-slate-800"
          >
            <Languages className="h-3.5 w-3.5" />
            {lang.toUpperCase()}
          </button>
          {user && (
            <button
              data-testid="logout-button"
              onClick={logout}
              className="p-1.5 rounded-md hover:bg-slate-800 text-slate-300"
              title="Se déconnecter"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
